import type { DecorationCss } from "./types";

const CUSTOM_PROPERTY = /^--[\w-]+$/;

const STANDARD_PROPERTY = /^-?[a-z]+(-[a-z0-9]+)*$/;

const DECLARATION_MEMBERS = new Set(["cssText", "getPropertyPriority", "getPropertyValue", "item", "length", "parentRule", "removeProperty", "setProperty"]);

const camelize = (property: string) => property.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());

const isSupported = (property: string) => typeof CSS === "undefined" || typeof CSS.supports !== "function" || CSS.supports(property, "initial");

const isStandardProperty = (property: string) => STANDARD_PROPERTY.test(property) && !DECLARATION_MEMBERS.has(camelize(property)) && isSupported(property);

export const isCssProperty = (property: string) => CUSTOM_PROPERTY.test(property) || isStandardProperty(property);

export const cssValueOf = (value: unknown): string | undefined => {
	if (typeof value === "string") return value;
	if (typeof value === "number" && Number.isFinite(value)) return String(value);
	return undefined;
};

const SHORTHAND_RANKS: Record<string, number> = Object.fromEntries([
	...[
		"inset",
		"margin",
		"padding",
		"border",
		"border-radius",
		"border-image",
		"background",
		"font",
		"flex",
		"grid",
		"gap",
		"place-content",
		"place-items",
		"place-self",
		"overflow",
		"outline",
		"transition",
		"animation",
		"text-decoration",
		"text-emphasis",
		"list-style",
		"columns",
		"column-rule",
		"mask",
		"offset",
		"overscroll-behavior",
		"scroll-margin",
		"scroll-padding",
		"container",
		"contain-intrinsic-size",
	].map((property) => [property, 0]),
	...[
		"inset-block",
		"inset-inline",
		"margin-block",
		"margin-inline",
		"padding-block",
		"padding-inline",
		"border-top",
		"border-right",
		"border-bottom",
		"border-left",
		"border-width",
		"border-style",
		"border-color",
		"border-block",
		"border-inline",
		"grid-template",
		"grid-area",
		"grid-row",
		"grid-column",
		"flex-flow",
		"font-variant",
		"scroll-margin-block",
		"scroll-margin-inline",
		"scroll-padding-block",
		"scroll-padding-inline",
	].map((property) => [property, 1]),
	...[
		"border-block-start",
		"border-block-end",
		"border-inline-start",
		"border-inline-end",
		"border-block-width",
		"border-block-style",
		"border-block-color",
		"border-inline-width",
		"border-inline-style",
		"border-inline-color",
	].map((property) => [property, 2]),
]);

const LONGHAND_RANK = 3;

const shorthandRank = (property: string) => SHORTHAND_RANKS[property.replace(/^-(webkit|moz|ms|o)-/, "")] ?? LONGHAND_RANK;

export const orderShorthandsFirst = (css: DecorationCss | undefined): DecorationCss | undefined => {
	if (!css) return css;
	const entries = Object.entries(css);
	const ordered = entries.map((entry, index) => ({ entry, index, rank: shorthandRank(entry[0]) })).sort((a, b) => a.rank - b.rank || a.index - b.index);
	if (ordered.every(({ index }, position) => index === position)) return css;
	return Object.fromEntries(ordered.map(({ entry }) => entry));
};
